import { useState } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { updateTenderOpeningStatus } from './useUpdateTenderOpeningStatus';
import { buildTenderOpeningDownloadFilename } from '../utils/tenderOpeningHelpers';

const getFilenameFromContentDisposition = contentDisposition => {
  if (!contentDisposition?.includes('filename=')) return null;
  return contentDisposition.split('filename=')[1].replace(/"/g, '').trim();
};

const useInviteDmut = ({
  procId,
  procurement,
  documents = [],
  onRefreshProcurement,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState({
    word: false,
    pdf: false,
  });
  const [searchText, setSearchText] = useState('');

  const sendInvitation = async () => {
    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return false;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${api.API_URL}/procurement/SaveNextInviteDMUT/${procId}`,
        {},
        {
          headers: {
            Accept: '*/*',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await updateTenderOpeningStatus(procId, 'isInviteDMUTCompleted', {
        maxRetries: 3,
        delayMs: 1000,
      });

      notify.success('Invitation to Registered Supplier sent successfully.');
      onRefreshProcurement?.();
      return true;
    } catch (err) {
      notify.apiError(err, 'send invitation to Registered Supplier');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateDocument = async type => {
    const token = getAuthToken();
    if (!token) {
      notify.error(MESSAGES.authRequired);
      return false;
    }
    if (!procId) {
      notify.error(
        'Procurement ID is missing. Refresh the page and try again.'
      );
      return false;
    }

    const endpoint =
      type === 'word'
        ? `${api.API_URL}/procurement/GenerateDocumentInviteDmutWord?procurementId=${procId}`
        : `${api.API_URL}/procurement/GenerateDocumentInviteDmutPDF?procurementId=${procId}`;

    const defaultFilename = buildTenderOpeningDownloadFilename({
      procurementNumber: procurement?.procurementNumber,
      title: 'Invite_Dmut',
      submittedDate: procurement?.submittedDateTenderOpening,
      extension: type === 'word' ? 'docx' : 'pdf',
    });

    setIsGeneratingDocument(prev => ({ ...prev, [type]: true }));
    try {
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let message = `Request failed with status code ${response.status}`;
        try {
          const errorData = await response.json();
          message = errorData?.message || message;
        } catch {
          // response body bukan JSON
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const filename =
        getFilenameFromContentDisposition(
          response.headers.get('Content-Disposition')
        ) || defaultFilename;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      notify.apiError(error, `generate ${type.toUpperCase()} document`);
      return false;
    } finally {
      setIsGeneratingDocument(prev => ({ ...prev, [type]: false }));
    }
  };

  const filteredDocuments = documents
    .filter(doc => doc.isShowToDMUT === true)
    .filter(doc => {
      const term = searchText.trim().toLowerCase();
      if (!term) return true;
      const name = (doc.documentName || doc.customFileName || '').toLowerCase();
      return name.includes(term);
    });

  return {
    documents: filteredDocuments,
    isSubmitting,
    isGeneratingDocument,
    searchText,
    setSearchText,
    sendInvitation,
    generateDocument,
  };
};

export default useInviteDmut;
